import { z } from "zod";
import type { BlockType } from "./types";
import type { LayoutBlock } from "./instance.schema";
import { BlockDefsMeta } from "./defs/blocks";
import { Regions, type Region } from "./defs/regions";

type LayoutBlockStrict = LayoutBlock & { type: BlockType };

function validateBlock(
  block: LayoutBlockStrict,
  region: Region,
  path: (string | number)[],
  ctx: z.RefinementCtx,
) {
  const def = BlockDefsMeta[block.type as BlockType];
  if (!def.allowedRegions.includes(region)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `"${block.type}" is not allowed in "${region}"`,
      path: [...path, "type"],
    });
  }
}

function countComponents(
  blocks: LayoutBlockStrict[],
  counter: Map<BlockType, number>
) {
  for (const block of blocks) {
    counter.set(block.type, (counter.get(block.type) || 0) + 1);
    if ("children" in block && Array.isArray(block.children)) {
      countComponents(block.children as LayoutBlockStrict[], counter);
    }
  }
}

export function refineLayout(
  regions: Record<Region, LayoutBlockStrict[]>,
  ctx: z.RefinementCtx
) {
  const componentCounter = new Map<BlockType, number>();
  
  for (const region of Regions as readonly Region[]) {
    countComponents(regions[region], componentCounter);
  }

  for (const [type, count] of componentCounter.entries()) {
    const def = BlockDefsMeta[type];
    if (def.maxPerLayout && def.maxPerLayout > 0 && count > def.maxPerLayout) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Layout is only permitted to have ${def.maxPerLayout} "${type}" instance(s), found ${count}`,
        path: ["regions"],
      });
    }
  }

  for (const region of Regions as readonly Region[]) {
    const blocks = regions[region];

    for (let i = 0; i < blocks.length; i++) {
      validateBlock(blocks[i]!, region, ["regions", region, i], ctx);
    }
  }
}